/**
 * Framerate-independent smoothing.
 *
 * Extracted from the scene code so it can be tested without a WebGL context —
 * everything else in src/game/ needs a real GPU. See `doc/grind/architecture/TESTING-STRATEGY.md`.
 */

/**
 * The blend factor for one frame of exponential smoothing.
 *
 * `rate` is how fast the value converges, in units of e-foldings per second. The
 * result is framerate-independent: two frames of dt/2 land exactly where one
 * frame of dt does, so a 144 Hz display and a 30 Hz one follow the same curve.
 */
export function smoothingAlpha(dt: number, rate: number): number {
  if (dt <= 0 || rate <= 0) return 0;
  return 1 - Math.exp(-rate * dt);
}

/** Linear interpolation, clamped so it can never overshoot. */
export function lerp(from: number, to: number, t: number): number {
  if (t <= 0) return from;
  if (t >= 1) return to;
  return from + (to - from) * t;
}

/**
 * Interpolates an angle along the shortest arc, so a heading crossing ±π spins
 * the short way round instead of unwinding a full turn.
 */
export function lerpAngle(from: number, to: number, t: number): number {
  const TAU = Math.PI * 2;
  let delta = (to - from) % TAU;
  if (delta > Math.PI) delta -= TAU;
  if (delta < -Math.PI) delta += TAU;
  return from + delta * Math.min(Math.max(t, 0), 1);
}
